import { useEffect, useState } from "react";
import { Check, TriangleAlert, Clock, Minus } from "lucide-react";

const fmtT = (s, tz) => { try { return new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true }).format(new Date(s * 1000)); } catch { return "—"; } };
const dur = (a, b) => { const m = Math.max(0, Math.round((b - a) / 60)); const h = Math.floor(m / 60); return h ? `${h}h ${m % 60}m` : `${m}m`; };

function normalize(items, now) {
  return items.map((it) => {
    const win = it.win;
    const na = !win || win === "nil" || win?.nil === true || (typeof win === "object" && win.start == null);
    const start = na ? null : win.start, end = na ? null : win.end;
    let state = "na";
    if (!na) state = now >= start && now < end ? "active" : now < start ? "upcoming" : "over";
    return { ...it, start, end, na, state };
  });
}

// Shared across Homepage, Free & Premium Panchangam.
// `nowOverride` (unix seconds) freezes the reference moment — used by Birth
// Panchangam so windows are judged against the birth time, not the clock.
export default function AuspiciousWindows({ items, sunrise, sunset, tz = "Asia/Kolkata", compact = false, only = null, nowOverride = null, atLabel = "Right now", markerLabel = "now", birthMode = false }) {
  const [now, setNow] = useState(nowOverride ?? Date.now() / 1000);
  useEffect(() => {
    if (nowOverride != null) { setNow(nowOverride); return; }
    const id = setInterval(() => setNow(Date.now() / 1000), 30000);
    return () => clearInterval(id);
  }, [nowOverride]);

  const all = normalize(items, now);
  const active = all.find((x) => x.state === "active");
  const next = all.filter((x) => x.state === "upcoming").sort((a, b) => a.start - b.start)[0];
  const good = all.filter((x) => x.tone === "good").sort((a, b) => (a.start || 9e9) - (b.start || 9e9));
  const bad = all.filter((x) => x.tone === "bad").sort((a, b) => (a.start || 9e9) - (b.start || 9e9));

  const t0 = sunrise || Math.min(...all.filter((x) => x.start).map((x) => x.start));
  const t1 = sunset || Math.max(...all.filter((x) => x.end).map((x) => x.end));
  const span = Math.max(1, t1 - t0);
  const pct = (v) => Math.min(100, Math.max(0, ((v - t0) / span) * 100));

  const Row = ({ x }) => (    <div data-testid={`aw-row-${x.key}`} className={`flex items-center gap-3 rounded-2xl border p-3.5 ${x.na ? "border-dashed border-white/10 bg-white/[0.015] opacity-70" : birthMode ? (x.state === "active" ? "border-amber-400/60 bg-amber-400/[0.08]" : "border-white/10 bg-white/[0.03]") : x.state === "active" ? (x.tone === "good" ? "border-emerald-400/60 bg-emerald-400/10" : "border-rose-400/60 bg-rose-400/10") : "border-white/10 bg-white/[0.03]"} ${!birthMode && x.state === "over" ? "opacity-55" : ""}`}>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${x.na ? "bg-white/5 text-white/30" : x.tone === "good" ? "bg-emerald-400/15 text-emerald-300" : "bg-rose-400/15 text-rose-300"}`}>
        {x.na ? <Minus className="h-4 w-4" /> : x.tone === "good" ? <Check className="h-4 w-4" /> : <TriangleAlert className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className={`text-[15px] font-medium ${x.na ? "text-white/55" : "text-white"}`}>{x.name}</p>
        {x.na ? (
          <p className="text-xs text-white/50">{x.naReason || "Not applicable today"}</p>
        ) : (
          <p className="text-[13px] text-white/60"><span className="dh-num">{fmtT(x.start, tz)} – {fmtT(x.end, tz)}</span> · {dur(x.start, x.end)}{x.naksh ? <span className="text-white/45"> · {x.naksh}</span> : null}</p>
        )}
        {!x.na && x.note && <p className="mt-0.5 text-xs text-white/45">{x.note}</p>}
        {!x.na && x.amber && <p className="mt-0.5 text-xs text-amber-300/80">{x.amber}</p>}
      </div>
      {!x.na && (birthMode ? (
        x.state === "active" ? (
          <span className="shrink-0 rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-black">At birth</span>
        ) : null
      ) : (
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${x.state === "active" ? "bg-amber-400 text-black" : x.state === "upcoming" ? "border border-white/20 text-white/70" : "text-white/40"}`}>
          {x.state === "active" ? "Active now" : x.state === "upcoming" ? "Upcoming" : "Over"}
        </span>
      ))}
    </div>
  );

  if (only) {
    const list = only === "good" ? good : bad;
    return (
      <div data-testid={`auspicious-${only}`} className="space-y-2">
        {list.length ? list.map((x) => <Row key={x.key} x={x} />) : <p className="py-2 text-sm text-white/45">Nothing to show.</p>}
      </div>
    );
  }

  return (
    <div data-testid="auspicious-windows" className="space-y-4">
      {/* Status banner */}
      <div className={`rounded-2xl border p-4 ${active ? (birthMode ? "border-amber-400/50 bg-amber-400/[0.08]" : active.tone === "good" ? "border-emerald-400/50 bg-emerald-400/10" : "border-rose-400/50 bg-rose-400/10") : "border-white/10 bg-white/[0.03]"}`} data-testid="aw-banner">
        <p className="flex items-center gap-2 text-sm font-medium text-white">
          <Clock className="h-4 w-4 text-white/60" />
          {active ? (birthMode
            ? <>{atLabel}: <b className="text-amber-300">{active.name}</b>{active.note ? ` — ${active.note.toLowerCase()}` : ""}. <span className="dh-num">{fmtT(active.start, tz)} – {fmtT(active.end, tz)}</span></>
            : <>{atLabel}: <b className={active.tone === "good" ? "text-emerald-300" : "text-rose-300"}>{active.name}</b>{active.note ? ` — ${active.note.toLowerCase()}` : ""}. Ends {fmtT(active.end, tz)}</>
          ) : `No special window ${atLabel === "At birth" ? "at birth" : "right now"}`}
        </p>
        {next && !birthMode && <p className="mt-1 pl-6 text-xs text-white/55">Next: {next.name} at {fmtT(next.start, tz)}</p>}
      </div>

      {/* Day timeline */}
      <div className="px-1" data-testid="aw-timeline">
        <div className="relative h-3 w-full rounded-full bg-white/10">
          {all.filter((x) => !x.na).map((x) => (
            <div key={x.key} title={x.name} className={`absolute top-0 h-3 rounded-full ${x.tone === "good" ? "bg-emerald-400/80" : "bg-rose-400/80"}`}
              style={{ left: `${pct(x.start)}%`, width: `${Math.max(1.5, pct(x.end) - pct(x.start))}%` }} />
          ))}
          <div className="absolute -top-1 h-5 w-0.5 bg-amber-400" style={{ left: `${pct(now)}%` }} />
        </div>
        <div className="mt-1 flex justify-between text-[10px] text-white/40"><span>{fmtT(t0, tz)}</span><span>{markerLabel}</span><span>{fmtT(t1, tz)}</span></div>
      </div>

      {!compact && (
        <>
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-300/80">Auspicious</p>
            <div className="space-y-2">{good.map((x) => <Row key={x.key} x={x} />)}</div>
          </div>
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-300/80">Avoid</p>
            <div className="space-y-2">{bad.map((x) => <Row key={x.key} x={x} />)}</div>
          </div>
        </>
      )}
      {compact && (
        <div className="space-y-2">{[...good, ...bad].filter((x) => x.state !== "over").slice(0, 2).map((x) => <Row key={x.key} x={x} />)}</div>
      )}
    </div>
  );
}
